import { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import CentersDropdown from '../../../components/CentersDropdown';
import CountryDropdown from '../../../components/CountryDropdown';
import StateDropdown from '../../../components/StateDropdown';
import CityDropdown from '../../../components/CityDropdown';
import ZonesDropdown from '../../../components/ZonesDropdown';
import RegionsDropdown from '../../../components/RegionsDropdown';

import {
	Gender,
	PreceptorsFilter,
	Country,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	COUNTRY_CODE_INDIA,
	Gender as GENDER,
	SNP_MODULE,
	VALID_ABHYASI_ID_REGEX,
	VALID_BATCH_REGEX,
	VALID_NAME_REGEX,
	VALID_PINCODE_REGEX,
} from '../../../shared/Constants';
import { useAuth } from '../../../hooks/useAuth';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { getLanguages } from '../../../service/MySrcmService';
import useUserSession from '../../../hooks/useUserSession';

interface FilterProps {
	value: PreceptorsFilter;
	onFilterChange: (value: PreceptorsFilter) => void;
	onDashboardDetailFilterChange?: (value: PreceptorsFilter) => void;
	isMentorsFilter?: boolean;
	formName: string;
	activeTabKey?: string | null;
	selectedBatchId: number | undefined;
	selectedModule?: string | null;
}

const { Text } = Typography;

interface RegionOption {
	value: number | undefined;
	label: string | undefined;
}
interface PreceptorsFilterFormType {
	srcmId?: string;
	name?: string;
	email?: string;
	mobile?: string;
	country?: string;
	stateId?: Country;
	cityId?: Country;
	centerId?: Country;
	countryId?: Country;
	postalCode?: string;
	regionNames?: RegionOption;
	zoneNames?: Country;
	batch?: string;
	gender?: Gender;
}

export const FilterPreceptors = ({
	value,
	onFilterChange,
	onDashboardDetailFilterChange,
	isMentorsFilter,
	formName,
	selectedBatchId,
	selectedModule,
}: FilterProps) => {
	const { t } = useTranslation();
	const { srcmId } = useAuth();
	const { showToastMessage } = useToastMessage();
	const { selectedRole } = useUserSession();
	const [form] = Form.useForm<PreceptorsFilterFormType>();
	const values = Form.useWatch<PreceptorsFilterFormType>([], form);
	const [languageOptions, setLanguageOptions] = useState<
		{ value: string; label: string }[]
	>([]);
	const [loadingLanguages, setLoadingLanguages] = useState<boolean>(true);
	const selectedRegion = Form.useWatch<Country>('regionNames', form);
	const selectedRegionId =
		typeof selectedRegion?.value === 'number'
			? selectedRegion.value
			: undefined;
	const isAdmin = selectedRole === UserRole.SNP_ADMIN;
	const isExamAdmin = selectedRole === UserRole.EXAM_ADMIN;
	const selectedZone = Form.useWatch<Country>('zoneNames', form);
	const zoneValueForCenters = selectedZone?.label;

	const handleFilterButtonClick = useCallback(() => {
		const errors = form.getFieldsError();
		const hasErrors = errors.some((field) => field.errors.length > 0);

		if (hasErrors) {
			showToastMessage('error', t('pleaseEnterValidData'));
			return;
		}

		const filterValues: PreceptorsFilter = {
			...values,
			countryIds: values?.countryId?.value
				? ([values?.countryId?.value] as Array<number>)
				: ([COUNTRY_CODE_INDIA] as Array<number>),
			centerIds: [values?.centerId?.value] as Array<number>,
			zoneNames: values?.zoneNames?.label as string,
			cityIds: [values?.cityId?.value] as Array<number>,
			stateIds: [values?.stateId?.value] as Array<number>,
			batch: values.batch as string,
		};

		if (isMentorsFilter) {
			const mentorsFilter = { ...value, ...filterValues };
			mentorsFilter.showMentors = true;
			onFilterChange(mentorsFilter);
		} else if (onDashboardDetailFilterChange) {
			const preceptorsFilter = { ...filterValues };
			preceptorsFilter.showAllPreceptors = true;
			preceptorsFilter.preceptorBatchId = selectedBatchId;
			onFilterChange(preceptorsFilter);

			const dashboardDetailFilter = { ...filterValues };
			dashboardDetailFilter.showPreceptorDashboardCardDetail = true;
			dashboardDetailFilter.preceptorBatchId = selectedBatchId;
			onDashboardDetailFilterChange(dashboardDetailFilter);
		} else {
			const preceptorsFilter = { ...filterValues };
			preceptorsFilter.showNewPreceptorsForGrouping = true;
			preceptorsFilter.preceptorBatchId = selectedBatchId;
			onFilterChange(preceptorsFilter);
		}
	}, [
		form,
		onFilterChange,
		onDashboardDetailFilterChange,
		value,
		values,
		isMentorsFilter,
		showToastMessage,
		t,
		selectedBatchId,
	]);

	const StateValue: string | number | undefined = values?.stateId?.value
		? values?.stateId?.value
		: '';

	const CountryValue: string | number | undefined = values?.countryId?.value
		? values?.countryId?.value
		: '';

	const handleResetForm = useCallback(() => {
		form.resetFields();
		const filterValues: PreceptorsFilter = {
			showAllPreceptors: value?.showAllPreceptors,
			showMentors: value?.showMentors,
			showNewPreceptorsForGrouping: value?.showNewPreceptorsForGrouping,
			showPreceptorDashboardCardDetail: value?.showPreceptorDashboardCardDetail,
			countryIds: [COUNTRY_CODE_INDIA],
			preceptorBatchId: selectedBatchId,
		};
		if (selectedModule === SNP_MODULE.Prework) {
			delete filterValues.preceptorBatchId;
			filterValues.batchId = selectedBatchId;
			filterValues.isPrework = true;
		} else {
			delete filterValues.batchId;
			filterValues.preceptorBatchId = selectedBatchId;
		}
		onFilterChange(filterValues);
		if (onDashboardDetailFilterChange) {
			const dashboardDetailFilter = { ...filterValues };
			dashboardDetailFilter.showPreceptorDashboardCardDetail = true;
			dashboardDetailFilter.preceptorBatchId = selectedBatchId;
			onDashboardDetailFilterChange(dashboardDetailFilter);
		}
	}, [
		form,
		value,
		onFilterChange,
		onDashboardDetailFilterChange,
		selectedBatchId,
		selectedModule,
	]);

	useEffect(() => {
		form.resetFields();
		const filterValues: PreceptorsFilter = {
			countryIds: [COUNTRY_CODE_INDIA],
			preceptorBatchId: selectedBatchId,
		};

		if (isMentorsFilter) {
			const mentorsFilter = { ...filterValues };
			if (selectedModule === SNP_MODULE.Prework) {
				delete mentorsFilter.preceptorBatchId;
				mentorsFilter.batchId = selectedBatchId;
				mentorsFilter.isPrework = true;
			} else {
				delete mentorsFilter.batchId;
				mentorsFilter.preceptorBatchId = selectedBatchId;
			}
			mentorsFilter.showMentors = true;
			onFilterChange(mentorsFilter);
		} else if (onDashboardDetailFilterChange) {
			const preceptorsFilter = { ...filterValues };
			preceptorsFilter.showAllPreceptors = true;
			onFilterChange(preceptorsFilter);

			const dashboardDetailFilter = { ...filterValues };
			dashboardDetailFilter.showPreceptorDashboardCardDetail = true;
			onDashboardDetailFilterChange(dashboardDetailFilter);
		} else {
			const preceptorsFilter = { ...filterValues };
			preceptorsFilter.showNewPreceptorsForGrouping = true;
			onFilterChange(preceptorsFilter);
		}
	}, [
		form,
		isMentorsFilter,
		onDashboardDetailFilterChange,
		onFilterChange,
		selectedBatchId,
		selectedModule,
	]);

	useEffect(() => {
		const fetchLanguages = async () => {
			try {
				const data = await getLanguages();
				const mapped = data.results.map((lang) => ({
					value: lang.name,
					label: lang.name,
				}));
				setLanguageOptions(mapped);
			} catch (err) {
				//
			} finally {
				setLoadingLanguages(false);
			}
		};

		void fetchLanguages();
	}, []);

	return (
		<div className="flex flex-col">
			<Form
				name={formName}
				layout="horizontal"
				size="middle"
				labelCol={{ span: 24 }}
				wrapperCol={{ span: 24 }}
				autoComplete="off"
				form={form}
			>
				<div className="flex flex-col mb-2 md:mb-0">
					<div className="flex flex-col gap-4 md:flex-row">
						{isMentorsFilter && (
							<>
								<div className="w-full md:w-1/6">
									<Text className="pl-1">{t('name')}</Text>
									<Form.Item
										name={'name'}
										rules={[
											{
												pattern: VALID_NAME_REGEX,
												message: (
													<span>
														{t('numbersAndSpecialCharactersNotAllowed')}
													</span>
												),
											},
										]}
										hasFeedback
									>
										<Input />
									</Form.Item>
								</div>
								<div
									className={`w-full ${
										isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'
									}`}
								>
									<Text className="pl-1">{t('abhyasiId')}</Text>
									<Form.Item
										name={'srcmId'}
										rules={[
											{
												pattern: VALID_ABHYASI_ID_REGEX,
												message: <span>{t('invalidAbhyasiId')}</span>,
											},
										]}
										hasFeedback
									>
										<Input />
									</Form.Item>
								</div>
							</>
						)}
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('batch')}</Text>
							<Form.Item
								name={'batch'}
								rules={[
									{
										pattern: VALID_BATCH_REGEX,
										message: <span>{t('validBatch')}</span>,
									},
								]}
								hasFeedback
							>
								<Input maxLength={8} />
							</Form.Item>
						</div>
						{(isAdmin || isExamAdmin) && (
							<div
								className={`w-full ${
									isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'
								}`}
							>
								<Text className="pl-1">{t('region')}</Text>
								<Form.Item name={'regionNames'} style={{ marginBottom: 2 }}>
									<RegionsDropdown
										functionarySrcmId={srcmId}
										multiple={false}
										onChange={(val) => {
											form.setFieldValue('regionNames', val);
											form.setFieldValue('zoneNames', undefined);
										}}
									/>
								</Form.Item>
								<Text className="mt-1 pl-1 text-xs text-gray-500">
									{t('selectRegionToSelectZones')}
								</Text>
							</div>
						)}
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('zones')}</Text>
							<Form.Item name={'zoneNames'}>
								<ZonesDropdown
									functionarySrcmId={srcmId}
									multiple={false}
									regionId={selectedRegionId}
									onChange={(val) => {
										form.setFieldValue('zoneNames', val);
										form.setFieldValue('centerId', undefined); // clear center on zone change
									}}
								/>
							</Form.Item>
						</div>
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('center')}</Text>
							<Form.Item name={'centerId'}>
								<CentersDropdown
									zoneValue={zoneValueForCenters}
									functionarySrcmId={srcmId}
									disabled={!zoneValueForCenters}
									onChange={(center) => {
										form.setFieldValue('centerId', center);
									}}
								/>
							</Form.Item>
						</div>
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('gender')}</Text>
							<Form.Item name={'gender'}>
								<Select
									options={[
										{
											value: GENDER.MALE,
											label: t('male'),
										},
										{
											value: GENDER.FEMALE,
											label: t('female'),
										},
									]}
								/>
							</Form.Item>
						</div>
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('language')}</Text>
							<Form.Item name={'language'}>
								<Select
									options={languageOptions}
									loading={loadingLanguages}
									showSearch
									allowClear
								/>
							</Form.Item>
						</div>
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('pincode')}</Text>
							<Form.Item
								name="postalCode"
								rules={[
									{
										pattern: VALID_PINCODE_REGEX,
										message: <span>{t('validPincode')}</span>,
									},
								]}
								hasFeedback
							>
								<Input maxLength={7} />
							</Form.Item>
						</div>
					</div>
					<div className="flex flex-col gap-4 md:flex-row">
						<div className="flex flex-col w-full min-w-[170px]">
							<Text className="pl-1">{t('country')}</Text>
							<Form.Item name={'countryId'}>
								<CountryDropdown />
							</Form.Item>
						</div>
						<div className="flex flex-col w-full min-w-[170px]">
							<Text className="pl-1">{t('state')}</Text>
							<Form.Item name={'stateId'}>
								<StateDropdown CountryValue={CountryValue} />
							</Form.Item>
						</div>
						<div className="flex flex-col w-full min-w-[170px]">
							<Text className="pl-1">{t('cityFilterName')}</Text>
							<Form.Item name={'cityId'}>
								<CityDropdown StateValue={StateValue && StateValue} />
							</Form.Item>
						</div>
					</div>
				</div>
			</Form>
			<div className="flex flex-row gap-2 md:mb-0">
				<div className="self-start my-2">
					<Button type="primary" onClick={handleFilterButtonClick}>
						{t('search')}
					</Button>
				</div>
				<div className="self-start my-2">
					<Button type="default" onClick={handleResetForm}>
						{t('clear')}
					</Button>
				</div>
			</div>
		</div>
	);
};
